import * as THREE from 'three'
import { maakPalletGeometrie } from './Lading'
import { useOppervlakken } from './useOppervlakken'
import { folie as folieTextuur } from './texturen'
import { useMaterialen, useOppervlak } from '../scene/store'

/**
 * Eén europallet met lading die van plek wisselt: op de C-MATIC, op de vorken
 * van een K- of R-MATIC, in een stellingvak. De vaste lading in de stellingen
 * (`Lading`) is instanced; deze niet, want hij beweegt en er zijn er maar een
 * handvol.
 */

export const SOORTEN = ['dozen', 'gewikkeld', 'kist', 'bigbag', 'vaten'] as const
export type Soort = (typeof SOORTEN)[number]

/** Een soort lading bij nummer n: vast per n, maar zonder herkenbaar patroon. */
export function soortVan(n: number): Soort {
  const h = Math.abs(Math.sin(n * 12.9898 + 4.1) * 43758.5453)
  return SOORTEN[Math.floor((h - Math.floor(h)) * SOORTEN.length)]
}

function kansVan(n: number, i: number) {
  const h = Math.abs(Math.sin(n * 78.233 + i * 19.19) * 24634.6345)
  return h - Math.floor(h)
}

/**
 * Geometrie en foliekaart delen alle pallets: ze zijn voor elke pallet
 * hetzelfde, en er wisselen er per rondgang een paar van inhoud. Ze leven
 * zo lang als de app.
 */
let gedeeld: { pallet: THREE.BufferGeometry; folie: THREE.Texture } | null = null
function gedeeldeDelen() {
  gedeeld ??= { pallet: maakPalletGeometrie(), folie: folieTextuur(17) }
  return gedeeld
}

/**
 * Een europallet met lading, onderkant op y = 0: 1200 langs x, 800 langs z,
 * zoals in de stelling en op de adapterplaat van de C-MATIC.
 *
 * `n` bepaalt de details (hoeveel lagen, hoe hoog, hoe scheef); `soort` legt
 * de soort lading vast, anders volgt die ook uit `n`.
 */
export function PalletMetLading({ n, soort: vast }: { n: number; soort?: Soort }) {
  const mat = useMaterialen()
  const opp = useOppervlak()
  const set = useOppervlakken()
  const { pallet: palletGeo, folie: folieMap } = gedeeldeDelen()

  const tex = (soort: 'hout' | 'karton' | 'doos', sterkte = 1) =>
    set
      ? {
          map: set[soort].map,
          roughnessMap: set[soort].roughnessMap,
          normalMap: set[soort].normalMap,
          normalScale: new THREE.Vector2(opp.relief * sterkte, opp.relief * sterkte),
        }
      : {}

  const soort = vast ?? soortVan(n)
  const r = (i: number) => kansVan(n, i)
  const top = 0.144

  return (
    <group>
      <mesh geometry={palletGeo} castShadow receiveShadow>
        <meshStandardMaterial color={mat.pallet} roughness={0.92} {...tex('hout')} />
      </mesh>

      {soort === 'dozen' && (
        // Twee of drie lagen dozen, elke laag net iets anders gezet.
        Array.from({ length: 2 + Math.floor(r(1) * 2) }, (_, laag) => {
          const h = 0.3 + r(10 + laag) * 0.18
          const y = top + Array.from({ length: laag }, (_, j) => 0.3 + r(10 + j) * 0.18).reduce((a, b) => a + b, 0)
          return [-0.28, 0.28].map((x) => (
            <mesh key={`${laag}${x}`} position={[x + (r(20 + laag) - 0.5) * 0.03, y + h / 2, (r(30 + laag) - 0.5) * 0.04]}
              castShadow receiveShadow>
              <boxGeometry args={[0.54, h, 0.76]} />
              <meshStandardMaterial color={mat.karton} roughness={0.88} {...tex('doos')} />
            </mesh>
          ))
        })
      )}

      {soort === 'gewikkeld' && (() => {
        const h = 0.95 + r(2) * 0.35
        return (
          <group position={[0, top + h / 2, 0]}>
            <mesh castShadow receiveShadow>
              <boxGeometry args={[1.08, h, 0.76]} />
              <meshStandardMaterial color={mat.karton} roughness={0.88} {...tex('doos')} />
            </mesh>
            {/* Wikkelfolie: glad, licht blauwig, en hij spiegelt de armaturen. */}
            <mesh scale={[1.03, 1.02, 1.04]}>
              <boxGeometry args={[1.08, h, 0.76]} />
              <meshStandardMaterial color="#d5e3ea" transparent alphaMap={folieMap} opacity={0.9} roughness={0.12}
                depthWrite={false} envMapIntensity={1.6} />
            </mesh>
            {[-0.3, 0.1].map((f) => (
              <mesh key={f} position={[0, f * h, 0]}>
                <boxGeometry args={[1.12, 0.025, 0.8]} />
                <meshStandardMaterial color="#25313d" roughness={0.6} />
              </mesh>
            ))}
          </group>
        )
      })()}

      {soort === 'kist' && (() => {
        const h = 0.7 + r(3) * 0.45
        return (
          <mesh position={[0, top + h / 2, 0]} castShadow receiveShadow>
            <boxGeometry args={[1.1, h, 0.78]} />
            <meshStandardMaterial color="#6d5a3f" roughness={0.85} {...tex('hout', 0.8)} />
          </mesh>
        )
      })()}

      {soort === 'bigbag' && (() => {
        const h = 0.9 + r(4) * 0.25
        return (
          <mesh position={[0, top + h / 2, 0]} rotation={[0, Math.PI / 4, 0]} scale={[1.05, 1, 0.74]} castShadow>
            <cylinderGeometry args={[0.52, 0.46, h, 4, 1]} />
            <meshStandardMaterial color="#d8d2c2" roughness={0.95} {...tex('karton', 0.7)} />
          </mesh>
        )
      })()}

      {soort === 'vaten' && [[-0.28, -0.19], [0.28, -0.19], [-0.28, 0.19], [0.28, 0.19]].map(([x, z], i) => (
        <mesh key={i} position={[x, top + 0.42, z]} rotation={[0, r(40 + i) * 3, 0]} castShadow>
          <cylinderGeometry args={[0.2, 0.2, 0.84, 16]} />
          <meshStandardMaterial color={r(5) > 0.5 ? '#2f6d8a' : '#3b3f45'} roughness={0.45} metalness={0.35} />
        </mesh>
      ))}
    </group>
  )
}
